<template>
  <div>
    <div v-if="mode === 'dashboard' && !programId" data-testid="leaderboard-empty">
      No leaderboard data available
    </div>
    <template v-else-if="mode === 'dashboard' && programId">
      <div v-if="loading" data-testid="dashboard-loading">Loading leaderboard...</div>
      <div v-else-if="error" data-testid="leaderboard-error">Unable to load leaderboard</div>
      <v-card v-else elevation="1" class="top-sales-leaders rounded-lg">
        <div class="leaderboard-header" data-testid="leaderboard-header">
          <h4>Leaderboard</h4>
          <button
            v-if="totalParticipants > 5"
            data-testid="see-all-link"
            class="see-all-btn"
            @click="navigateToAllParticipants"
            @keydown.enter="navigateToAllParticipants"
          >
            See All
          </button>
        </div>
        <section class="leaderboard" data-testid="leaderboard-participants">
          <div
            v-for="(participant, index) in ranked"
            :key="participant.programCacheId || index"
            class="leaderboard-row"
            :class="{ 'active-row': participant.isCurrentUser }"
            :style="
              participant.isCurrentUser
                ? { '--row-highlight': highlightColorValue, height: '56px', borderRadius: '4px' }
                : { height: '56px', borderRadius: '4px' }
            "
          >
            <div
              :data-testid="
                participant.isCurrentUser
                  ? 'participant-rank-circle-active'
                  : 'participant-rank-circle'
              "
              class="rank"
              :aria-label="`Rank ${participant.rank}`"
            >
              {{ participant.rank }}
            </div>
            <img
              v-if="participant.avatarUrl"
              :src="participant.avatarUrl"
              :alt="participant.displayName"
              data-testid="participant-avatar-img"
              class="avatar-img"
            />
            <div v-else class="avatar" data-testid="participant-avatar-fallback">
              <span>{{ (participant.displayName || "").charAt(0) }}</span>
            </div>
            <span data-testid="participant-name" class="name">{{
              participant.isCurrentUser ? "You" : participant.displayName
            }}</span>
            <span data-testid="participant-progress" class="amount"
              >{{ participant.progressPercent }}%</span
            >
          </div>
          <v-alert
            v-if="userRank === null && !previewMode"
            type="info"
            data-testid="unranked-alert"
            class="mt-2"
          >
            Your rank is still being calculated, check back soon!
          </v-alert>
        </section>
      </v-card>
    </template>
    <v-card v-else-if="loading || ranked.length > 0" elevation="0" class="align-text-left">
      <span class="mediumGrey--text mb-2" v-if="version != 2">Leaderboard</span>
      <div v-if="loading" class="d-flex justify-center">
        <lottie
          :options="lottieOptions"
          :height="170"
          :width="170"
          v-on:animCreated="handleAnimation"
        />
      </div>
      <div v-else>
        <v-virtual-scroll
          :bench="10"
          :items="ranked"
          :height="virtualScrollHeight"
          item-height="56"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.programCacheId" class="ma-0 pa-0">
              <div
                v-if="item.userId == userProfile.userId"
                class="active-row d-flex align-center justify-space-between pa-1 full-width ma-0"
              >
                <div class="d-flex align-center">
                  <div class="active-rank text-align-center d-flex align-center justify-center">
                    <b>{{ item.rank ? item.rank : "-" }}</b>
                  </div>
                  <b class="ml-2">{{ item.name }}</b>
                </div>
                <b class="mr-4" v-if="item.progressPercent == null"> - </b>
                <b class="mr-5" v-else>{{ item.progressPercent }}%</b>
              </div>
              <div v-else class="not-active-row d-flex flex-row align-center mr-3">
                <div
                  class="mr-2 not-active-rank text-align-center d-flex align-center justify-center grey--text"
                >
                  <span>{{ item.rank ? item.rank : "-" }}</span>
                </div>
                <div
                  class="pl-2 d-flex justify-space-between align-center light-grey-background full-width pa-1"
                >
                  <span>{{ item.name }}</span>
                  <span class="mr-4" v-if="item.progressPercent == null"> - </span>
                  <span class="mr-2" v-else>{{ item.progressPercent }}%</span>
                </div>
              </div>
            </v-list-item>
          </template>
        </v-virtual-scroll>
      </div>
    </v-card>
  </div>
</template>

<script>
import ProgramService from "@/services/ProgramService.js";
import Lottie from "vue-lottie/src/lottie.vue";
import * as animationData from "@/assets/robin/Eating.json";
import { mapState } from "vuex";
export default {
  name: "Leaderboard",
  components: { Lottie },
  directives: {
    activeRowStyle: {
      bind(el, binding) {
        if (binding.value) el.setAttribute("style", binding.value);
      },
      update(el, binding) {
        if (binding.value) el.setAttribute("style", binding.value);
        else el.removeAttribute("style");
      },
    },
  },
  props: {
    programId: String,
    smallVersion: Boolean,
    version: Number,
    mode: {
      type: String,
      default: "details",
    },
    config: {
      type: Object,
      default: null,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      lottieOptions: {
        animationData: animationData.default,
        loop: true,
        autoplay: true,
      },
      anim: null,
      animationSpeed: 1,
      loading: true,
      programCache: [],
      ranked: [],
      totalParticipants: 0,
      userRank: null,
      error: false,
    };
  },
  created() {
    if (this.mode === "details" && this.programId !== undefined && this.programId !== null) {
      this.getProgramCache();
    } else if (this.mode === "dashboard" && this.programId) {
      if (!this.previewMode) {
        this.fetchLeaderboardV3();
      } else {
        this.ranked = [
          {
            programCacheId: "mock-1",
            rank: 1,
            displayName: "Alex M.",
            progressPercent: 92,
            avatarUrl: null,
            isCurrentUser: false,
          },
          {
            programCacheId: "mock-2",
            rank: 2,
            displayName: "Jordan S.",
            progressPercent: 78,
            avatarUrl: null,
            isCurrentUser: true,
          },
          {
            programCacheId: "mock-3",
            rank: 3,
            displayName: "Taylor R.",
            progressPercent: 71,
            avatarUrl: null,
            isCurrentUser: false,
          },
          {
            programCacheId: "mock-4",
            rank: 4,
            displayName: "Casey P.",
            progressPercent: 60,
            avatarUrl: null,
            isCurrentUser: false,
          },
          {
            programCacheId: "mock-5",
            rank: 5,
            displayName: "Morgan L.",
            progressPercent: 54,
            avatarUrl: null,
            isCurrentUser: false,
          },
        ];
        this.totalParticipants = 12;
        this.loading = false;
      }
    }
  },
  methods: {
    handleAnimation: function (anim) {
      this.anim = anim;
    },
    async fetchLeaderboardV3() {
      try {
        const response = await ProgramService.getLeaderboardV3(this.programId);
        if (response && response.data) {
          this.ranked = response.data.participants;
          this.totalParticipants = response.data.totalParticipants;
          this.userRank = response.data.userRank;
          this.loading = false;
        } else {
          this.loading = false;
          this.error = true;
        }
      } catch (e) {
        this.loading = false;
        this.error = true;
      }
    },
    getProgramCache() {
      this.loading = true;
      ProgramService.getProgramLeaderboard(this.programId)
        .then((programCache) => {
          this.loading = false;
          this.programCache = programCache;
          this.ranked = programCache;
        })
        .catch(() => {
          this.loading = false;
          this.error = true;
        });
    },
    navigateToAllParticipants() {
      this.$router.push({
        name: "programdetails",
        params: { programId: this.programId, sourcePage: "programs" },
      });
    },
  },
  computed: {
    ...mapState(["userProfile"]),
    highlightColorValue() {
      return this.config && this.config.highlightColor ? this.config.highlightColor : "#FFDCEF";
    },
    virtualScrollHeight() {
      return Math.min(this.ranked.length, 5) * 56;
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
/* ─── Dashboard mode — Figma spec ─── */
.top-sales-leaders {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 24px 16px !important;
  width: 100%;
}

.leaderboard-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  gap: 8px;
  padding: 0 0 0 8px;
}

.leaderboard-header h4 {
  font-family: "Avenir", "Avenir Next", sans-serif;
  font-weight: 500;
  font-size: 22px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.87);
  flex: 1;
  margin: 0;
  text-align: left;
}

.see-all-btn {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 18px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: "Avenir", "Avenir Next", sans-serif;
  font-weight: 350;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  text-align: center;
  color: #077daf;
}

.leaderboard {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  align-self: stretch;
  gap: 8px;
  margin-top: 8px;
}

.leaderboard-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  align-self: stretch;
  gap: 12px;
  padding: 8px;
  border-radius: 4px;
}

.rank {
  font-family: "Avenir", "Avenir Next", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
  min-width: 20px;
  text-align: right;
}

.avatar {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 6px 5px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 24px;
  flex-shrink: 0;
}

.avatar span {
  font-family: "Lato", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
  color: #ffffff;
}

.name {
  font-family: "Avenir", "Avenir Next", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
  flex: 1;
  text-align: left;
}

.amount {
  font-family: "Avenir", "Avenir Next", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
  text-align: right !important;
}

/* ─── Details mode (existing) ─── */
.active-row {
  background: var(--row-highlight);
}
.active-rank {
  background-color: var(--v-accent-base);
  width: 30px;
  height: 30px;
}
.not-active-row {
  width: 94%;
}
.not-active-rank {
  width: 30px;
  height: 30px;
}
.light-grey-background {
  background-color: var(--v-lightGrey-base);
}
.v-list__tile {
  padding: 0;
}
.avatar-fallback {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}
.avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}
</style>
